/**
 * src/components/EnhancedSavedDocuments.tsx — the sidebar: Preambles-first tabs, a folder tree above
 * a folder-scoped item list, per-row Move/Delete, and the dnd-kit drag & drop root.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/EnhancedSavedDocuments.tsx` (whole file,
 *         487 lines).
 * Spec:   `docs/spec/06-frontend-components.md` §9 (all of it), §9.1 `headerOnly`, §9.2 full mode,
 *         §9.3 drag & drop, §15 D-5/D-6/D-16/D-17/D-18, §16 ranks 5/8/12.
 *
 * **Two instances are mounted at once** by `ConverterApp`: `headerOnly` in the header
 * (`ConverterApp.tsx:236-240`) and the full sidebar (`:269-273`).
 *
 * ═══ LAYOUT IS FIXED — DO NOT REDESIGN (spec §16 "Layout stability requirement") ══════════════
 * `defaultValue="preambles"` — **Preambles is the landing tab**, and the tab order is
 * Preambles then Documents (`:169-173`). Each tab body is two stacked equal cards
 * (`flex-1 basis-0`): the **folder tree above**, the item list below. That is the shipped
 * information architecture.
 *
 * ═══ DF-22 DEFECTS REPRODUCED HERE ════════════════════════════════════════════════════════════
 *
 *  - **rank 5 — `isError` IS NOT RENDERED.** Both list bodies (`:222-281` documents, `:331-400`
 *    preambles) branch on `isLoading` and then on `data && data.length > 0`, falling through to the
 *    empty state. `useDocumentsByFolder`/`usePreamblesByFolder` expose `isError`; nothing here reads
 *    it, and `src/lib/api.ts` has already discarded the server's message. So an expired JWT (Worker
 *    answers `403 {"error":"Invalid token."}`) or a stopped Worker renders **"No documents in this
 *    folder"** — a server outage is indistinguishable from an empty account. `Admin.tsx:50` holds
 *    the app's only error state and it stays the only one. Do not add error panels or Retry here.
 *
 *  - **rank 8 — no folder-cycle check on drag.** `handleDragEnd`'s folder branch guards only
 *    `activeData.id === overData.id` (`:124`); the `TODO` at `:126-127` admits the rest. Drag a
 *    parent onto its own child and the Worker accepts it (verified live: `5→6→5`, 200, no error);
 *    `FolderTree.buildFolderTree` then drops every node in the cycle, so **both folders vanish from
 *    the tree with no message and no way back through the UI.** The ancestor walk is the fix.
 *
 *  - **rank 12 — hover-only actions and an anonymous drag overlay.** Move/Delete live behind
 *    `opacity-0 group-hover:opacity-100` (`:244`, `:363`) — invisible on touch, unreachable by
 *    keyboard. The `DragOverlay` (`:471-482`) reads the literal **"Moving item..."** and never names
 *    what is being dragged. Note the Delete button carries the opacity classes a *second* time,
 *    nested inside the already-hidden wrapper (`:264`, `:383`) — redundant in the source, preserved.
 *
 *  - **rank 7 — a folder dragged onto Root** sends `parent_id: null`, which the Worker's
 *    `COALESCE(?, parent_id)` ignores, yet `'Folder moved successfully'` still fires (`:133`).
 *
 *  - **rank 2's neighbourhood** — clicking a document calls `onLoadDocument(…, doc.id, doc.title)`.
 *    `ConverterApp.handleLoadDocument` accepts the id and throws it away, and never threads it into
 *    the save dialog, which is why re-saving duplicates. That omission is `ConverterApp`'s; this
 *    file passes the id faithfully.
 *
 *  - **rank 10 — the breadcrumb.** `useFolder(selectedFolder || 0)` at `:66-67` reads
 *    `['folder', id]`, which `useUpdateFolder`'s `['folders']` invalidation never prefix-matches, so
 *    a renamed folder keeps its old name here. Also note the breadcrumb is a **single level**, not a
 *    path (`:211`, `:321`), and `|| 0` disables the query at Root so `'Root'` comes from a ternary.
 *
 *  - **D-16 (spec §15) — `headerOnly` still runs the full hook set**, because the early return is at
 *    `:151`, below the ten hooks at `:62-84`. The header instance therefore fires its own
 *    `useDocumentsByFolder`, `usePreamblesByFolder`, two disabled `useFolder(0)` queries and five
 *    mutations. React Query dedupes the identical keys; the doubled subscriptions remain. Splitting
 *    the user chip into its own component is the fix (spec §15 D-16). Do not.
 *
 * ═══ DF-20 — `html_content` NO LONGER EXISTS ══════════════════════════════════════════════════
 * The source called `onLoadDocument(doc.latex_content, doc.html_content || '', doc.id, doc.title)`
 * (`:140`). The column is gone (owner-approved), so the second argument is `''` — **the identical
 * runtime value**, since `doc.html_content` was already `undefined` on the by-folder endpoint's
 * projection in every case where it mattered, and `ConverterApp.handleLoadDocument` ignores the
 * parameter entirely (spec §4.3). The four-argument prop signature is unchanged.
 */
import { useState, useRef, type CSSProperties, type ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useDocumentsByFolder, useDeleteDocument, usePreamblesByFolder, useDeletePreamble, useFolder, useUpdateDocument, useUpdatePreamble, useUpdateFolder } from '@/hooks/use-database'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Trash2, FileText, Code, ChevronRight, Plus, Move, Folder } from 'lucide-react'
import { FolderTree, type FolderTreeHandle } from './FolderTree'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { MoveDialog } from './MoveDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { DndContext, type DragEndEvent, type DragStartEvent, DragOverlay, useDraggable, useSensor, useSensors, PointerSensor } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { toast } from 'sonner'
import type { DocumentByFolderItem, Preamble } from '@/lib/types'

/** Props — `EnhancedSavedDocuments.tsx:26-30`, field for field. */
interface EnhancedSavedDocumentsProps {
  onLoadDocument?: (latexContent: string, htmlContent: string, documentId?: number, documentTitle?: string) => void
  onLoadPreamble?: (preambleContent: string, preambleName?: string) => void
  headerOnly?: boolean
}

/**
 * What the `data` of a dnd-kit draggable carries. The source read `event.active.data.current`
 * untyped (`strictNullChecks: false` made every field `any`); the shape is exactly what
 * `DraggableItem` below and `FolderTree`'s `useDraggable` put there.
 */
interface DragSourceData {
  type: 'document' | 'preamble' | 'folder'
  id: number
  parentId?: number | null
}

/**
 * What a droppable carries. Only folders are droppable, and the Root pseudo-folder uses
 * `id: null` (`FolderTree.tsx:345-348`) — that `null` is what makes a drop on Root send
 * `parent_id: null` / `folder_id: null`.
 */
interface DropTargetData {
  type: 'folder'
  id: number | null
}

/** `EnhancedSavedDocuments.tsx:32-50`. `touchAction: 'none'` is required by `PointerSensor`. */
const DraggableItem = ({ id, type, children, className }: { id: number; type: 'document' | 'preamble'; children: ReactNode; className?: string }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${type}-${id}`,
    data: { type, id }
  })

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    touchAction: 'none', // Required for PointerSensor
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={className}>
      {children}
    </div>
  )
}

export const EnhancedSavedDocuments = ({ onLoadDocument, onLoadPreamble, headerOnly = false }: EnhancedSavedDocumentsProps) => {
  const { user, logout } = useAuth()
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null)
  const [selectedPreambleFolder, setSelectedPreambleFolder] = useState<number | null>(null)
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [itemToMove, setItemToMove] = useState<{ id: number; type: 'document' | 'preamble'; currentFolderId: number | null } | null>(null)
  const [documentToDelete, setDocumentToDelete] = useState<number | null>(null)
  const [preambleToDelete, setPreambleToDelete] = useState<number | null>(null)
  const [activeDragItem, setActiveDragItem] = useState<DragSourceData | null>(null)

  // DF-22 rank 5 — `isError` is deliberately NOT destructured from either query. See file header.
  const { data: documents, isLoading: documentsLoading } = useDocumentsByFolder(selectedFolder)
  const { data: preambles, isLoading: preamblesLoading } = usePreamblesByFolder(selectedPreambleFolder)

  // Fetch folder details for breadcrumbs
  // DF-22 rank 10 — key `['folder', id]`, never invalidated by `useUpdateFolder`'s `['folders']`.
  const { data: currentFolder } = useFolder(selectedFolder || 0)
  const { data: currentPreambleFolder } = useFolder(selectedPreambleFolder || 0)

  const { mutate: deleteDocument } = useDeleteDocument()
  const { mutate: deletePreamble } = useDeletePreamble()
  const { mutate: updateDocument } = useUpdateDocument()
  const { mutate: updatePreamble } = useUpdatePreamble()
  const { mutate: updateFolder } = useUpdateFolder()

  const folderTreeRef = useRef<FolderTreeHandle>(null)
  const preambleFolderTreeRef = useRef<FolderTreeHandle>(null)

  // `distance: 8` is why a click still loads an item instead of starting a drag (spec §9.3).
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragItem((event.active.data.current as DragSourceData | undefined) ?? null)
  }

  /**
   * `:90-136`.
   *
   * ⚠️ **DF-22 rank 8 is the folder branch below.** The only guard is `activeData.id ===
   * overData.id`. Dropping a folder onto one of its own descendants is accepted, creating a
   * `parent_id` cycle that erases both folders from the tree (`FolderTree.buildFolderTree`).
   * The `TODO` is the source's, kept word for word.
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveDragItem(null)

    if (!over) return

    const activeData = active.data.current as DragSourceData | undefined
    const overData = over.data.current as DropTargetData | undefined

    if (!activeData || !overData) return

    // Moving Document or Preamble to Folder
    if ((activeData.type === 'document' || activeData.type === 'preamble') && overData.type === 'folder') {
      const targetFolderId = overData.id // null for root

      if (activeData.type === 'document') {
        updateDocument({
          id: activeData.id,
          updates: { folder_id: targetFolderId }
        }, {
          onSuccess: () => toast.success('Document moved successfully')
        })
      } else {
        updatePreamble({
          id: activeData.id,
          updates: { folder_id: targetFolderId }
        }, {
          onSuccess: () => toast.success('Preamble moved successfully')
        })
      }
    }

    // Moving Folder to Folder
    if (activeData.type === 'folder' && overData.type === 'folder') {
      if (activeData.id === overData.id) return // Cannot move to self

      // TODO: Prevent circular dependency (check if target is child of source)
      // For now, backend might handle or we just rely on basic check
      //
      // DF-22 rank 8 — reproduced verbatim. The backend does NOT handle it
      // (`worker/routes/folders.ts` has no ancestor walk), so this comment's hope is unfounded and
      // the cycle is created. Adding the check here is the fix. Do not.

      updateFolder({
        id: activeData.id,
        // DF-22 rank 7 — `overData.id` is `null` for a drop on Root, which the Worker's
        // `parent_id = COALESCE(?, parent_id)` discards while still returning 200.
        updates: { parent_id: overData.id }
      }, {
        onSuccess: () => toast.success('Folder moved successfully')
      })
    }
  }

  /**
   * `:138-142`. DF-20: the second argument was `doc.html_content || ''`; the column no longer
   * exists, so it is `''` — the same value the expression already produced. `ConverterApp`
   * discards both this argument and `doc.id` (spec §4.3, DF-22 rank 2).
   */
  const handleLoadDocument = (doc: DocumentByFolderItem) => {
    if (onLoadDocument) {
      onLoadDocument(doc.latex_content, '', doc.id, doc.title)
    }
  }

  /** `:144-148`. */
  const handleLoadPreamble = (preamble: Preamble) => {
    if (onLoadPreamble) {
      onLoadPreamble(preamble.content, preamble.name)
    }
  }

  // If headerOnly mode, just show user info
  // NOTE the position of this early return: every hook above still runs in the header instance.
  // That is spec §15 D-16, reproduced — see the file header.
  if (headerOnly) {
    return (
      <div className="flex items-center gap-3">
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Logged in as</p>
          <p className="text-sm font-semibold">{user?.username}</p>
        </div>
        <Button variant="outline" size="sm" onClick={logout}>
          Logout
        </Button>
      </div>
    )
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="h-full flex flex-col">

        {/* Preambles-first tab order and Preambles as the landing tab — spec §16. */}
        <Tabs defaultValue="preambles" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0 mb-4">
            <TabsTrigger value="preambles">Preambles</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents" className="flex-1 m-0 min-h-0 gap-4 data-[state=active]:flex data-[state=active]:flex-col data-[state=inactive]:hidden">
            {/* Folder Tree */}
            <Card className="flex-1 basis-0 flex flex-col min-h-0">
              <CardHeader className="py-3 flex-shrink-0">
                <CardTitle className="text-sm">Folders</CardTitle>
              </CardHeader>
              <CardContent className="py-2 flex-1 overflow-hidden flex flex-col">
                <ScrollArea className="flex-1">
                  <FolderTree
                    ref={folderTreeRef}
                    // Dead prop — `FolderTree` never reads it (spec §10.1). Passed because the
                    // source passes it, and only on this tab.
                    onLoadDocument={onLoadDocument}
                    onFolderSelect={setSelectedFolder}
                    hideCreateButton={true}
                  />
                </ScrollArea>
                <div className="pt-2 mt-auto">
                  {/* Creates a SUBFOLDER of the currently selected folder, not a root folder. */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => folderTreeRef.current?.openCreateDialog(selectedFolder)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Folder
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Documents in Selected Folder */}
            <Card className="flex-1 basis-0 flex flex-col min-h-0">
              <CardHeader className="flex-shrink-0">
                {/* Single-level breadcrumb, not a path — spec §10.3. */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <FileText className="h-4 w-4" />
                  <ChevronRight className="h-3 w-3" />
                  <span>{selectedFolder === null ? 'Root' : (currentFolder?.name || 'Loading...')}</span>
                </div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Documents
                </CardTitle>
                <CardDescription>
                  {documents?.length || 0} document{documents?.length !== 1 ? 's' : ''} in this folder
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                {/* DF-22 rank 5 — two branches only. A failed fetch falls through to the empty
                    state below and reads as "No documents in this folder". */}
                {documentsLoading ? (
                  <p className="text-sm text-muted-foreground">Loading documents...</p>
                ) : documents && documents.length > 0 ? (
                  <ScrollArea className="h-full">
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <DraggableItem
                          key={doc.id}
                          id={doc.id}
                          type="document"
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors group bg-card"
                        >
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => handleLoadDocument(doc)}
                          >
                            <p className="font-medium">{doc.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {/* `created_at` is nullable on the wire. Cast, do not guard — a guard
                                  would change what renders. `new Date(null)` is the epoch in JS, so
                                  a null date prints 01/01/1970 exactly as it does today. */}
                              {new Date(doc.created_at as string).toLocaleDateString()}
                              {doc.preamble_name && ` • ${doc.preamble_name}`}
                            </p>
                          </div>
                          {/* DF-22 rank 12 — hover-only actions. */}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation()
                                setItemToMove({ id: doc.id, type: 'document', currentFolderId: doc.folder_id })
                                setMoveDialogOpen(true)
                              }}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Move className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation()
                                setDocumentToDelete(doc.id)
                              }}
                              // The opacity pair repeats inside the already-hidden wrapper. Source's.
                              className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive transition-opacity"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </DraggableItem>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center p-4">
                    <FileText className="h-12 w-12 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No documents in this folder</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Save a document to get started
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preambles Tab */}
          <TabsContent value="preambles" className="flex-1 m-0 min-h-0 gap-4 data-[state=active]:flex data-[state=active]:flex-col data-[state=inactive]:hidden">
            {/* Folder Tree for Preambles */}
            <Card className="flex-1 basis-0 flex flex-col min-h-0">
              <CardHeader className="py-3 flex-shrink-0">
                <CardTitle className="text-sm">Folders</CardTitle>
              </CardHeader>
              <CardContent className="py-2 flex-1 overflow-hidden flex flex-col">
                <ScrollArea className="flex-1">
                  {/* No `onLoadDocument` here — the source omits it on this tab only. */}
                  <FolderTree
                    ref={preambleFolderTreeRef}
                    onFolderSelect={setSelectedPreambleFolder}
                    hideCreateButton={true}
                  />
                </ScrollArea>
                <div className="pt-2 mt-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => preambleFolderTreeRef.current?.openCreateDialog(selectedPreambleFolder)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Folder
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Preambles in Selected Folder */}
            <Card className="flex-1 basis-0 flex flex-col min-h-0">
              <CardHeader className="flex-shrink-0">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Code className="h-4 w-4" />
                  <ChevronRight className="h-3 w-3" />
                  <span>{selectedPreambleFolder === null ? 'Root' : (currentPreambleFolder?.name || 'Loading...')}</span>
                </div>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  Preambles
                </CardTitle>
                <CardDescription>
                  {preambles?.length || 0} preamble{preambles?.length !== 1 ? 's' : ''} in this folder
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                {/* DF-22 rank 5 again — no `isError` branch. */}
                {preamblesLoading ? (
                  <p className="text-sm text-muted-foreground">Loading preambles...</p>
                ) : preambles && preambles.length > 0 ? (
                  <ScrollArea className="h-full">
                    <div className="space-y-2">
                      {preambles.map((preamble) => (
                        <DraggableItem
                          key={preamble.id}
                          id={preamble.id}
                          type="preamble"
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors group bg-card"
                        >
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => handleLoadPreamble(preamble)}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium break-words">{preamble.name}</p>
                              {/* DF-22 rank 16 — this badge and the `(Default)` suffix in
                                  `SaveDocumentDialog` are the ONLY readers of `is_default`. Nothing
                                  auto-loads the default preamble. */}
                              {preamble.is_default && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded flex-shrink-0">
                                  Default
                                </span>
                              )}
                            </div>
                            {preamble.description && (
                              <p className="text-xs text-muted-foreground mt-1 break-words">{preamble.description}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(preamble.created_at as string).toLocaleDateString()}
                            </p>
                          </div>
                          {/* DF-22 rank 12 — hover-only actions. */}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation()
                                setItemToMove({ id: preamble.id, type: 'preamble', currentFolderId: preamble.folder_id })
                                setMoveDialogOpen(true)
                              }}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Move className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation()
                                setPreambleToDelete(preamble.id)
                              }}
                              className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive transition-opacity"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </DraggableItem>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center p-4">
                    <Code className="h-12 w-12 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No preambles saved yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Save a preamble to reuse it later
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {itemToMove && (
          <MoveDialog
            open={moveDialogOpen}
            onOpenChange={setMoveDialogOpen}
            sourceId={itemToMove.id}
            sourceType={itemToMove.type}
            currentParentId={itemToMove.currentFolderId}
            onMoveSuccess={() => {
              // React query will handle invalidation
            }}
          />
        )}

        {/* Delete Document Alert Dialog */}
        <AlertDialog open={!!documentToDelete} onOpenChange={(open) => !open && setDocumentToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the document.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (documentToDelete) {
                    deleteDocument(documentToDelete)
                    setDocumentToDelete(null)
                  }
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Preamble Alert Dialog */}
        <AlertDialog open={!!preambleToDelete} onOpenChange={(open) => !open && setPreambleToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the preamble.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (preambleToDelete) {
                    deletePreamble(preambleToDelete)
                    setPreambleToDelete(null)
                  }
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* DF-22 rank 12 — the overlay never names the item being dragged. */}
        <DragOverlay>
          {activeDragItem ? (
            <div className="p-3 border rounded-lg bg-background shadow-lg opacity-80 w-[300px]">
              <div className="flex items-center gap-2">
                {activeDragItem.type === 'document' ? <FileText className="h-4 w-4" /> :
                  activeDragItem.type === 'preamble' ? <Code className="h-4 w-4" /> :
                    <Folder className="h-4 w-4" />}
                <span className="font-medium">Moving item...</span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </div>
    </DndContext>
  )
}
